import React, { useMemo, useState } from 'react';
import { Box, Button, TextField } from '@mui/material';
import { format as formatSQL } from 'sql-formatter';
import { ApprovalCard, CodeBlock } from '../../ui-kit';
import type { ApprovalAction } from '../core/approvals';
import type { SQLApproval } from '../../pages/copilot/stores/useCopilotStore';
import { SQLApprovalCard } from '../../pages/copilot/components/WorkspacePanel';

export interface SqlApprovalProps {
  approval: SQLApproval;
  onDecide: (action: ApprovalAction, editedSql?: string, reason?: string) => void;
  /** `inline` — in the conversation. `pane` — in a results pane, with room for more of the query. */
  variant?: 'inline' | 'pane';
}

/** A query waiting to run: read it, edit it, or say no. */
export default function SqlApproval({ approval, onDecide, variant = 'inline' }: SqlApprovalProps) {
  const formatted = useMemo(() => {
    try {
      return formatSQL(approval.sql);
    } catch {
      return approval.sql;
    }
  }, [approval.sql]);
  // `editing` — changing the query before it runs. `rejecting` — saying why
  // it should not run, so the agent can try something else.
  const [step, setStep] = useState<'idle' | 'editing' | 'rejecting'>('idle');
  const [draft, setDraft] = useState(formatted);
  const [reason, setReason] = useState('');
  const editing = step === 'editing';

  // File and API requests carry more than SQL; their card knows how to show it.
  if (approval.isFileConnector || approval.isApiConnector) {
    return (
      <SQLApprovalCard
        approval={approval}
        variant={variant === 'pane' ? 'full' : 'inline'}
        onApprove={() => onDecide('approve')}
        onEdit={(sql: string) => onDecide('edit', sql)}
        onReject={(reason: string) => onDecide('reject', undefined, reason)}
      />
    );
  }

  return (
    <ApprovalCard
      title="SQL approval required"
      source={approval.connectorName}
      description={approval.title}
      actions={
        step === 'editing' ? (
          <>
            <Button size="small" color="inherit" onClick={() => { setStep('idle'); setDraft(formatted); }}>Cancel</Button>
            <Box sx={{ flex: 1 }} />
            <Button size="small" variant="contained" disabled={!draft.trim()} onClick={() => onDecide('edit', draft)}>
              Run edited query
            </Button>
          </>
        ) : step === 'rejecting' ? (
          <>
            <Button size="small" color="inherit" onClick={() => { setStep('idle'); setReason(''); }}>Cancel</Button>
            <Box sx={{ flex: 1 }} />
            <Button size="small" variant="contained" color="error" onClick={() => onDecide('reject', undefined, reason.trim() || undefined)}>
              Reject query
            </Button>
          </>
        ) : (
          <>
            <Button size="small" color="inherit" onClick={() => setStep('rejecting')}>Reject</Button>
            <Box sx={{ flex: 1 }} />
            <Button size="small" variant="outlined" onClick={() => setStep('editing')}>Edit</Button>
            <Button size="small" variant="contained" onClick={() => onDecide('approve')}>Approve &amp; Run</Button>
          </>
        )
      }
    >
      <CodeBlock
        code={editing ? draft : formatted}
        language="sql"
        lineNumbers
        copyable
        onChange={editing ? setDraft : undefined}
        maxHeight={variant === 'pane' ? 480 : 240}
        aria-label={editing ? 'Edit the SQL' : 'SQL to run'}
      />
      {step === 'rejecting' && (
        // Saying why is optional, but it is what lets the agent try a
        // different query instead of simply stopping.
        <TextField
          label="Why? (optional)"
          placeholder="e.g. this table is stale — use klaris.lead_v instead"
          fullWidth
          multiline
          minRows={2}
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mt: 1.5 }}
        />
      )}
    </ApprovalCard>
  );
}
